// BUILD-042 October 10 12:24 addendum — "Assigned date is not Signed date... cannot relabel
// assigned/upload/review date as signed date. Show actual manually reported paper sign date
// only when present with truthful label; otherwise Reviewed date or Uploaded date/unknown. No
// automatic Executed/electronic Verified signature claim; reviewed image remains HR-reviewed
// file."
//
// Single shared source of truth for "what date, and what honest label, goes next to this
// document row" — used by BOTH AdminDocumentsSection (HR) and DocumentsSelfSection (employee)
// so the two views can never silently drift into showing different, or incorrect, date
// semantics for the exact same underlying record. This picks the MOST SPECIFIC fact actually
// on the record, in order, and never invents one:
//   1. Employee's own manually-reported paper signing date, if they provided it — labeled as
//      exactly what it is (reported, not verified).
//   2. HR's review date, once reviewed.
//   3. The upload timestamp, if a scan exists but has not been reviewed yet.
//   4. The assignment date, as a LAST resort — never relabeled as anything else.
//   5. The cancellation date, if cancelled.
import type { HrDocumentAssignment, HrDocumentSubmissionMeta } from '../../../api/client';
// October 10 22:28 addendum: reuses the SAME date-only-safe, MM/DD/YYYY formatter the rest of
// HR uses — no second/parallel formatter introduced here. Every dateText below previously
// rendered a short-month style ("Jan 15, 2026") via a bare toLocaleDateString() with no day-
// shift protection; formatLocalDate both fixes the format and reuses the existing noon-anchored
// parse that already protects date-only values elsewhere in this app.
import { formatLocalDate } from '../hrFormat';

export interface DocumentDateLabel {
  label: string;
  dateText: string;
}

export function documentDateLabel(a: HrDocumentAssignment, currentSubmission: HrDocumentSubmissionMeta | undefined): DocumentDateLabel {
  if (a.status === 'cancelled') {
    return { label: 'Cancelled', dateText: a.cancelledAt ? formatLocalDate(a.cancelledAt) : 'date unknown' };
  }
  if (currentSubmission?.reportedSignedDate) {
    // The employee's own hand-written date on the paper, as they typed it in — never an
    // electronic signature or verification claim. reportedSignedDate is a date-only value
    // (YYYY-MM-DD), so this now also gets the same day-shift-safe MM/DD/YYYY formatting
    // instead of being shown as the raw stored string.
    return { label: 'Signed on paper (as reported, not verified)', dateText: formatLocalDate(currentSubmission.reportedSignedDate) };
  }
  if (a.status === 'reviewed_complete' && currentSubmission?.reviewedAt) {
    return { label: 'Reviewed', dateText: formatLocalDate(currentSubmission.reviewedAt) };
  }
  if (currentSubmission?.uploadedAt) {
    return { label: 'Uploaded', dateText: formatLocalDate(currentSubmission.uploadedAt) };
  }
  return { label: 'Assigned', dateText: formatLocalDate(a.assignedAt) };
}
